import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CalendarBookingDisplayItem, TimeZoneInfoDisplayItem } from '@sb/library/data-models';
import { FormFieldDateBaseComponent } from '@sb/library/form-field';
import { SBDate, SBDateTime } from '@sb/library/localization';
import { StyleManager } from '@sb/library/managers';
import { MbscCalendarLabel, MbscDateType, MbscDatepicker, MbscDatepickerChangeEvent, MbscDatepickerPageLoadingEvent } from '@mobiscroll/angular';

@Component({
    selector: 'lib-slot-picker',
    templateUrl: './slot-picker.component.html',
    styleUrl: './slot-picker.component.scss'
})
export class SlotPickerComponent extends FormFieldDateBaseComponent<SBDateTime> {

    @ViewChild(MbscDatepicker) datePicker!: MbscDatepicker;

    @Output() dateTimeChanged: EventEmitter<SBDateTime> = new EventEmitter();
    @Output() timezoneChange: EventEmitter<TimeZoneInfoDisplayItem> = new EventEmitter();

    private _bookingSlots!: CalendarBookingDisplayItem[];
    private _uniqueDates: Set<string> = new Set();
    private _timeFormat: string = this._LocalizationService.ShortTimePattern.replace('tt', 'a');
    private _slotsByDateMap: Record<string, CalendarBookingDisplayItem[]> = {};

    @Input() timeZones: TimeZoneInfoDisplayItem[] = [];

    private _currentTimezone?: TimeZoneInfoDisplayItem;
    @Input()
    set currentTimezone(value: TimeZoneInfoDisplayItem | undefined) {
        this._currentTimezone = value;
        if (value) {
            this.TimezoneChange(value.ID!);
        }
    }

    @Input()
    set bookingSlots(slots: CalendarBookingDisplayItem[]) {
        this._bookingSlots = slots;
        if (slots?.length) {
            this.AssignSlotLabels();
        }
    }

    private _labels: MbscCalendarLabel[] = [];
    private _validSlots: Array<MbscDateType> = [];
    private _currentDate: SBDate = SBDate.Today();

    constructor(private _StyleManager: StyleManager) {
        super();
    }

    // You select a specific date
    DateChangeEvent(event: MbscDatepickerChangeEvent): void {
        // for some reason mobiscroll triggers this over and over again when you pass in an empty array into the valid input
        const eventDate = SBDateTime.FromJSDate(event.value as Date);
        if (!this._validSlots.length || !this._slotsByDateMap[eventDate.ToISO()]) {
            return;
        }

        this.formControl.setValue(eventDate, { emitModelToViewChange: false });
        this.dateTimeChanged.emit(eventDate);
        this._currentDate = eventDate as SBDate;
    }

    PageLoading(event: MbscDatepickerPageLoadingEvent): void {
        // We are navigating using Mobiscroll Navigation buttons
        const date = SBDate.FromJSDate(event.month as Date);
        if (this._currentDate.month !==  date.month) {
            this.SetTempVal(date);
        }
    }

    TimezoneChange(timezoneID: string, userChange: boolean = false): void {
        if (this.timeZones.length) {
            this._currentTimezone = this.GetCurrentSelectionByID(timezoneID);
        }
        if (this.datePicker?.value) {
            this._currentDate = SBDate.FromJSDate(this.datePicker.value);
            this.SetTempVal(SBDate.FromComponents(this._currentDate.year, this._currentDate.month, 1));
        }
        if (userChange) {
            this.timezoneChange.emit(this._currentTimezone);
        }
    }

    GetCurrentSelectionByID(id: string): TimeZoneInfoDisplayItem {
        return this.timeZones.find(timeZone => timeZone.ID == id)!;
    }

    IsDateInvalid(date: string | undefined): boolean {
        if (date === undefined) {
            return false;
        }
        return !this._uniqueDates.has(date);
    }

    private SetTempVal(navigateToDate: SBDate): void {
        const today = SBDate.Today();
        if (navigateToDate.month !== today.month) {
            this.datePicker.setTempVal(navigateToDate.ToISO());
        } else {
            this.datePicker.setTempVal(today.ToISO());
        }
    }

    private AssignSlotLabels(): void {
        const valid: Array<MbscDateType> = [];
        this._slotsByDateMap = {};
        this._uniqueDates = new Set<string>();
        const labels: MbscCalendarLabel[] = [];
        const sortedBookingSlots = this._bookingSlots.sort((a,b) => {
            return a.StartDate.localeCompare(b.StartDate)
        })
        const firstValidDate = sortedBookingSlots[0].StartDate;

        this._bookingSlots?.forEach(slot => {
            const date = slot.StartDate.split('T')[0];
            const labelDate = SBDate.FromISO(date).ToJSDate();
            const textColor = this._StyleManager.GetCSSPropertyValue('--primary-brand-500');
            if (!this._uniqueDates.has(date)) {
                labels.push({
                    start: labelDate,
                    title: '•',
                    textColor: textColor
                });
                this._uniqueDates.add(date);
            }
            valid.push({start: slot.StartDate, end: slot.StartDate});
            if (this._slotsByDateMap[slot.StartDate]) {
                this._slotsByDateMap[slot.StartDate].push(slot);
            } else {
                this._slotsByDateMap[slot.StartDate] = [slot];
            }
        });

        this._labels = labels;
        this._validSlots = valid;

        this.datePicker.setTempVal(firstValidDate);
        this.DateChangeEvent({value: new Date(SBDateTime.FromISO(firstValidDate).ToJSDate())});

    }

    get validSlots(): Array<MbscDateType> {
        return this._validSlots;
    }

    get labels(): MbscCalendarLabel[] {
        return this._labels;
    }

    get bookingSlots(): CalendarBookingDisplayItem[] {
        return this._bookingSlots;
    }

    get SBDateTime(): typeof SBDateTime {
        return SBDateTime;
    }

    get currentTimezone(): TimeZoneInfoDisplayItem | undefined {
        return this._currentTimezone;
    }

    get timeFormat(): string {
        return this._timeFormat;
    }

    get currentDate(): SBDate {
        return this._currentDate;
    }

}
